/**
 * Replay engine: drives a Game in replay mode from a recorded input log, and
 * the headless verifier used by the golden suite and (later) server-side
 * verification. Step order matches the prototype's frame loop exactly:
 * skip-check, apply due actions, step the sim, then compare keyframes.
 */
import { DT, REPLAY_VERSION_1, REPLAY_VERSION_2, REPLAY_VERSION_3 } from '../data/balance.js';
import { Game, type GameRules } from './game.js';
import type { Keyframe, Replay, RunResult } from './types.js';

/**
 * Sim rules for a recording (ADR 0007): v1/v2 replays were recorded when the
 * run ended at wave 50; v3 runs are endless and end at death or retire.
 */
export function rulesForVersion(version: string): GameRules {
  return { endsAtLastWave: version !== REPLAY_VERSION_3 };
}

export interface Desync {
  readonly t: number;
  readonly expected: number;
  readonly actual: number;
}

export interface ReplayPlayerOptions {
  /**
   * Fast-forward inert stretches (the prototype's watch-mode behavior). It
   * re-syncs gt to tick * DT, whereas live play accumulates gt by repeated
   * += DT -- visually irrelevant, but not bit-identical. Watch mode wants it;
   * verification must not use it.
   */
  readonly skipIdle: boolean;
}

export class ReplayPlayer {
  readonly game: Game;
  private readonly skipIdle: boolean;
  private actionIdx = 0;
  private kfIdx = 0;
  readonly desyncs: Desync[] = [];

  constructor(
    readonly replay: Replay,
    opts: ReplayPlayerOptions = { skipIdle: false },
  ) {
    this.skipIdle = opts.skipIdle;
    this.game = new Game({
      seed: replay.seed,
      startedAt: replay.startedAt,
      player: replay.player,
      mode: 'replay',
      rules: rulesForVersion(replay.version),
    });
  }

  get desynced(): boolean {
    return this.desyncs.length > 0;
  }

  /** All recorded inputs have been applied. */
  get actionsDone(): boolean {
    return this.actionIdx >= this.replay.actions.length;
  }

  /** All recorded keyframes have been checked. */
  get keyframesDone(): boolean {
    return this.kfIdx >= this.replay.keyframes.length;
  }

  /**
   * Fast-forward idle stretches: when the board is inert (build/choose, no
   * creeps) and the next recorded action is far away, jump straight to it.
   * Deterministically safe -- nothing sim-relevant evolves during inert ticks
   * (gt is re-synced to tick).
   */
  private maybeSkip(): void {
    const acts = this.replay.actions;
    if (this.actionIdx >= acts.length) return;
    const nextT = acts[this.actionIdx]!.t;
    const g = this.game;
    if (
      (g.phase === 'build' || g.phase === 'choose') &&
      g.creeps.length === 0 &&
      g.spawnQueue === 0 &&
      nextT > g.tick + 45
    ) {
      g.tick = nextT - 30;
      g.gt = g.tick * DT;
    }
  }

  private applyDueActions(): void {
    const acts = this.replay.actions;
    while (this.actionIdx < acts.length && acts[this.actionIdx]!.t <= this.game.tick) {
      applyReplayAction(this.game, acts[this.actionIdx++]!);
    }
  }

  private checkDesync(): void {
    const kfs = this.replay.keyframes;
    while (this.kfIdx < kfs.length && kfs[this.kfIdx]!.t <= this.game.tick) {
      const kf: Keyframe = kfs[this.kfIdx++]!;
      if (kf.t === this.game.tick) {
        const actual = this.game.stateHash();
        if (kf.hash !== actual) this.desyncs.push({ t: kf.t, expected: kf.hash, actual });
      }
    }
  }

  /** Advance one fixed timestep of replay playback. */
  stepTick(): void {
    if (this.skipIdle) this.maybeSkip();
    this.applyDueActions();
    this.game.stepTick();
    this.checkDesync();
  }
}

/** Apply one recorded action to a game (shared by replay, resume, verify). */
export function applyReplayAction(g: Game, a: Replay['actions'][number]): void {
  switch (a.op) {
    case 'place':
      g.placeStone(a.x!, a.y!);
      break;
    case 'pickup':
      g.pickupStone(a.x!, a.y!);
      break;
    case 'keep':
      g.actionKeep(a.i!);
      break;
    case 'combine':
      g.actionCombine(a.i!, a.n ?? 2);
      break;
    case 'recipe':
      g.actionRecipe(a.i!, a.key!);
      break;
    case 'upgrade':
      g.upgradeChance();
      break;
    case 'lift':
      g.liftPiece(a.x!, a.y!);
      break;
    case 'drop':
      g.dropPiece(a.x!, a.y!);
      break;
    case 'slate':
      g.actionSlate(a.i!, a.sk!, a.x!, a.y!);
      break;
    case 'slateUp':
      g.actionSlateUp(a.x!, a.y!, a.x2!, a.y2!);
      break;
    case 'retire':
      g.actionRetire();
      break;
  }
}

/**
 * Rebuild a live, record-mode game from a saved run (a replay prefix + the
 * tick it was saved at). Deterministic re-simulation: the resumed game is
 * bit-identical to one that was never interrupted -- locked by a unit test.
 */
export function resumeGame(prefix: Replay, atTick: number): Game {
  const g = new Game({
    seed: prefix.seed,
    startedAt: prefix.startedAt,
    player: prefix.player,
    mode: 'record',
    rules: rulesForVersion(prefix.version),
  });
  g.restoring = true;
  let ai = 0;
  while (g.tick < atTick) {
    while (ai < prefix.actions.length && prefix.actions[ai]!.t <= g.tick) {
      applyReplayAction(g, prefix.actions[ai++]!);
    }
    g.stepTick();
  }
  g.restoring = false;
  // The prefix's actions carry their original tick stamps; keyframes were
  // re-recorded identically during the restore run above.
  g.recording.actions.push(...prefix.actions);
  g.drainEvents(); // restore-time events were already shown when they happened
  return g;
}

export interface VerifyReport {
  readonly ok: boolean;
  readonly desyncs: readonly Desync[];
  readonly keyframesChecked: number;
  readonly recordedResult: RunResult | null;
  readonly simulatedResult: RunResult | null;
  readonly ticksSimulated: number;
  readonly error?: string;
}

/** Basic structural validation before spending CPU on re-simulation. */
export function isReplayShaped(value: unknown): value is Replay {
  if (typeof value !== 'object' || value === null) return false;
  const r = value as Record<string, unknown>;
  return (
    (r['version'] === REPLAY_VERSION_1 ||
      r['version'] === REPLAY_VERSION_2 ||
      r['version'] === REPLAY_VERSION_3) &&
    typeof r['seed'] === 'number' &&
    typeof r['startedAt'] === 'string' &&
    (r['player'] === 'human' || r['player'] === 'agent') &&
    Array.isArray(r['actions']) &&
    Array.isArray(r['keyframes'])
  );
}

/**
 * Headlessly re-simulate a replay and compare every keyframe hash and the
 * final result against what was recorded. The golden suite calls this over
 * the prototype corpus; the server will call it (or its structural subset)
 * on submission.
 */
export function verifyReplay(replay: Replay): VerifyReport {
  const player = new ReplayPlayer(replay);
  const g = player.game;
  // Generous bound: the recorded run's own length plus 20 sim-minutes for the
  // final wave to resolve. A healthy replay terminates well inside it.
  const lastRecordedTick = Math.max(
    replay.actions.length > 0 ? replay.actions[replay.actions.length - 1]!.t : 0,
    replay.keyframes.length > 0 ? replay.keyframes[replay.keyframes.length - 1]!.t : 0,
  );
  const maxTick = lastRecordedTick + 36000;

  try {
    for (;;) {
      const finished =
        player.actionsDone &&
        player.keyframesDone &&
        (g.phase === 'gameover' || g.recording.result !== null);
      if (finished) break;
      if (g.tick >= maxTick) {
        return {
          ok: false,
          desyncs: player.desyncs,
          keyframesChecked: replay.keyframes.length,
          recordedResult: replay.result,
          simulatedResult: g.recording.result,
          ticksSimulated: g.tick,
          error: `did not terminate within ${maxTick} ticks`,
        };
      }
      player.stepTick();
    }
  } catch (e) {
    return {
      ok: false,
      desyncs: player.desyncs,
      keyframesChecked: replay.keyframes.length,
      recordedResult: replay.result,
      simulatedResult: g.recording.result,
      ticksSimulated: g.tick,
      error: `re-simulation threw: ${e instanceof Error ? e.message : String(e)}`,
    };
  }

  const sim = g.recording.result;
  const rec = replay.result;
  const resultMatches =
    rec !== null &&
    sim !== null &&
    rec.outcome === sim.outcome &&
    rec.wave === sim.wave &&
    rec.lives === sim.lives;

  return {
    ok: !player.desynced && resultMatches,
    desyncs: player.desyncs,
    keyframesChecked: replay.keyframes.length,
    recordedResult: rec,
    simulatedResult: sim,
    ticksSimulated: g.tick,
  };
}
